/* KANON `Raporty` — the survey column, transcribed from `01-ankiety.html`:
 *   column   392px fixed, 18px between the two groups
 *   eyebrow  10.5 / 700 / .14em, uppercase, --text-3
 *   card     radius 16, 14 15 padding, 9px gap, --bg-card under a --border hairline
 *   title    15 / 800
 *   chip     22px tall, 0 9 padding, radius 14, --bg-chip over --text-2, 11.5 / 600
 *   meta     12.5 / 600, --text-2, with the status word inline
 *   roster   11.5 / 600 beside the avatar stack, flush right
 *
 * ── The wait is the cards it is about to be ──────────────────────────────────────────────────
 * Owner, 2 September 2026: „szkielet w kształcie treści". The skeleton uses the card's own class,
 * so the reserve is the card's own geometry rather than a number copied beside it — and the gate
 * hides the BARS, never the box. `--h-report-card` and `--h-report-card-intake` state the two
 * heights so a card is the same height loading and loaded.
 *
 * ── A paused form DIMS, and the word says so too ─────────────────────────────────────────────
 * The canon's `.62` opacity. It is never the only channel: the meta line reads „wstrzymana" in
 * words, which is what a greyscale display and a screen reader both get.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.column {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-5);
  width: var(--w-report-column);
  min-height: 0;
  overflow-y: auto;
}

.group {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-10px);
}

/* A group's name, in sentence case and the quiet ink — owner, 4 November 2026. It used to wear the
 * canon's tracked uppercase eyebrow, which shouted a two-word label louder than the cards under it. */
.groupLabel {
  margin: var(--space-0);
  font-size: var(--type-label);
  font-weight: var(--fw-semibold);
  line-height: var(--type-label-lh);
  color: var(--text-2);
}

.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

/* The card is the drawn BOX; `.cardButton` inside it is the target. See the component for why the
 * two were split — the canon's switch cannot stand inside a button. */
.card {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: var(--h-report-card);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-16px);
}

.cardButton {
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  gap: var(--space-9px);
  width: 100%;
  height: 100%;
  padding: var(--space-14px) var(--space-15px);
  font-family: var(--font-body);
  color: inherit;
  text-align: start;
  cursor: pointer;
  background: none;
  border: 0;
}

/* The room the switch stands in, inside the button, so the title and the chip stop where the canon
 * stops them. `--space-2` is the head row's own gap, which the slot has to make up for. */
.headSlot {
  flex: none;
  width: var(--sz-toggle-w);
}

/* The switch itself, over that room. `--space-14px` / `--space-15px` are the card's own padding. */
.toggleSlot {
  position: absolute;
  top: var(--space-14px);
  right: var(--space-15px);
  display: inline-flex;
  align-items: center;
  height: var(--h-report-chip);
}

/* The mandatory form has no roster row, so its card is the shorter reserve. */
.cardIntake {
  height: var(--h-report-card-intake);
}

.card:hover {
  border-color: var(--border-2);
}

/* The ring is drawn on the CARD, because the button fills it exactly and a ring on the inner box
 * would be hidden by the card's own rounded corner. */
.card:has(.cardButton:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The previewed form. A brighter edge AND `aria-current` on the button — the state is never
 * carried by the border alone.
 *
 * TWO classes, and that is not decoration: `.card:hover` above is a (0,2,0) selector and this one
 * would be (0,1,0), so the pointer resting on the card it had just chosen painted the SELECTED edge
 * as the HOVER edge and the two states became one. Measured in the running app — the selected card
 * computed `rgba(255,255,255,0.2)`, which is `--border-2`, the hover value. */
.card.cardOn {
  border-color: var(--border-3);
}

/* Wstrzymana. See the header: the word carries it too. */
.cardPaused {
  opacity: 0.62;
}

.head {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-report-title);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The intake's chip: a lock and the MoveBuddy lockup, painted through `public/brand/lockup.png` as
 * a mask in the chip's own ink. 82x13 is the file's 640x102 at a 13px cap; a mask has no intrinsic
 * size to derive it from. */
.brandLockup {
  flex: none;
  width: 82px;
  height: 13px;
  background-color: var(--text-2);
  mask: url('/brand/lockup.png') left center / contain no-repeat;
}

.meta {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--type-control);
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.chevron {
  display: block;
  flex: none;
  width: var(--sz-report-glyph-md);
  height: var(--sz-report-glyph-md);
  margin-inline-start: auto;
  color: var(--text-3);
}

.roster {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
}

.rosterCount {
  flex: none;
  font-size: var(--type-control);
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.rosterFaces {
  margin-inline-start: auto;
}

/* ── The three states with no cards ──────────────────────────────────────────────────────────
 * All of them stand in the column's own box, so its width and its place never change with its
 * state. */

.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: var(--space-6) var(--space-5);
  text-align: center;
}

/* The bars inside a waiting card. Their widths are layout — the names they stand in for are not all
 * the same length either. */
.skelTitle {
  width: 58%;
  height: 12px;
}

.skelChip {
  width: 84px;
  height: 14px;
}

.skelMeta {
  width: 44%;
  height: 10px;
}

.skelFace {
  width: var(--sz-report-face);
  height: var(--sz-report-face);
}
